import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { useParams } from "react-router-dom";
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import { AxiosRequestConfig } from "axios";
import { AXIOS_INSTANCE, BASE_URL } from "../../App";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import { Bar } from "react-chartjs-2";
import TableAbstraction from "../../componentUtils/TableAbstraction"

interface CustomerRouteParams {
  identifier: string;
}

interface ReportingState {
  isLoaded: boolean,
  isLoading: boolean,
  dateFrom: string,
  dateTo: string,
  chartData: any,
  tableData: any,
}

interface MatDesc {
  materialNum: string,
  materialNumShort: string,
  description: string
}

interface CounterData {
  label: string;
  data: number[];
}

interface CounterResp {
  labels: string[];
  data: CounterData[];
  matDesc: MatDesc[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    typography: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    cont: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    grid: {
      marginLeft: theme.spacing(1),
    },
    load: {
      textAlign: "center",
      justify: "center",
    },
  })
);


async function getReportingData(salesOrg: string, customerNum: string, dateFrom: string, dateTo: string) {
  const configReporting: AxiosRequestConfig = {
    method: "GET",
    params: {
      clientId: "100",
      salesOrg: salesOrg,
      customerNum: customerNum,
      dateFrom: dateFrom,
      dateTo: dateTo
    },
    url: `${BASE_URL}/api/reporting/vmdexcounter`,
  };

  return await AXIOS_INSTANCE(configReporting).then((res: any) => res.data);
}

export default function SalesReporting(props: {
  customerNum?: string;
}) {
  const classes = useStyles();
  const theme = useTheme();



  const [state, setState] = useState<ReportingState>({
    isLoaded: false,
    isLoading: false,
    dateFrom: "",
    dateTo: "",
    chartData: [],
    tableData: [],
  });

  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const routeParams = useParams<CustomerRouteParams>();

  const customerNum = routeParams.identifier || "";

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true
    },
    layout: {
      padding: 0
    },
    scales: {
      x: { stacked: true, },
      y: { stacked: true,
            gridLines: {
            borderDash: [2],
            borderDashOffset: [2],
            color: theme.palette.divider,
            drawBorder: false,
            zeroLineBorderDash: [2],
            zeroLineBorderDashOffset: [2],
            zeroLineColor: theme.palette.divider
          }
        }
    }
  }

  function colorPal(num: number):Array<string> {
    const colors = ['#1f77b4', '#aec7e8', '#ff7f0e', '#ffbb78', "2ca02c",
                    '#98df8a', '#d62728', '#ff9896', '#9467db', '#c5b0d5',
                    '#8c564b', '#c49c94', '#e377c2', '#f7b6d2', '#7f7f7f',
                    '#c7c7c7', '#bcbd22', '#dbdb8d', '#17becf', '#9edae5'];
    var pal = Array<string>();
    for(var i=0; i<num; i++) {
      let index = (i % colors.length);
      pal[i] = colors[index];
    }
    return pal;
  }

  const loadData = (dateFrom: string, dateTo: string) => {
    getReportingData(salesOrg.toString(), customerNum, dateFrom, dateTo)
      .then((v) => {
       // console.log(v);
        const counterResp: CounterResp = v;
        var chartdata: any = null;
        var tableData = new Array<any>();
        var tableRow: any = null;
        if (counterResp.data) {
          const colors2 = colorPal(counterResp.data.length);
          var datasets = new Array<any>();
          const matDescArr:Array<MatDesc> = v.matDesc;
          counterResp.data.forEach((element, i) => {
            var desc = "";
            const matDesc = matDescArr.find(elem => elem.materialNum === element.label);
            if (matDesc) {
              desc = matDesc.description;
            }
            const shortNum = element.label.replace(/^0+/, "");
            let label = shortNum + " - " + desc;
            datasets[i] = {label: label, data: element.data, backgroundColor: colors2[i]};
            // table Data
            element.data.forEach((val, index) => {
              tableRow = {custnr: customerNum, label: counterResp.labels[index], matnr: shortNum, desc: desc, qty: val};
              tableData.push(tableRow);
            });
          })
          chartdata = {labels: counterResp.labels, datasets: datasets};
        }
       // console.log(chartdata);
       // console.log(tableData);
        setState({...state, isLoaded: true, isLoading: false, dateFrom: dateFrom, dateTo: dateTo, chartData: chartdata, tableData: tableData})
      })
  }

  useEffect(() => {
    if (state.isLoaded) return;
    if (!customerNum) return;

    const today = new Date();
    var oneMonth = new Date();
    oneMonth.setDate(oneMonth.getDate() - 30);
    const dateFrom = oneMonth.toISOString().split("T")[0];
    const dateTo = today.toISOString().split("T")[0];

    loadData(dateFrom, dateTo);

  }, [state]);

  const handleDateFromChange = (event: any) => {
    event.persist();
    loadData(event.target.value, state.dateTo);
  }

  const handleDateToChange = (event: any) => {
    event.persist();
    loadData(state.dateFrom, event.target.value);
  }

  if (!state.isLoaded) {
    return null;
  }

  return (
    <ResponsiveDrawer
      title="Reporting"
    >
      <div className={classes.rootContainer}>
        <Paper className={classes.cont}>
        <Grid className={classes.grid} container spacing={3}>
        <Grid item lg={2} md={2} xl={2} xs={2}>
          <TextField label="Date From" fullWidth={true} type="date" value={state.dateFrom} onChange={(ce)=>handleDateFromChange(ce)}/>
        </Grid>
        <Grid item lg={2} md={2} xl={2} xs={2}>
          <TextField label="Date To" fullWidth={true} type="date" value={state.dateTo} onChange={(ce)=>handleDateToChange(ce)}/>
        </Grid>
        </Grid>
        </Paper>
        <Paper className={classes.cont}>
          <Bar type='bar' height={800} options={options} data={state.chartData} />
        </Paper>
        <Paper className={classes.cont}>
        <TableAbstraction
          settingsKey="RPT_DXS"
                      options={{
                        serverSide: false,
                        responsive: "vertical",
                        selectableRows: "none",
                        tableHeightReducePx: 0,
                        rowsPerPage: 100,
                      }}
                      title="Daily Sales Data"
                      columns={[
                        {
                          label: "Time Period",
                          name: "label",
                        },
                        {
                          label: "Customer #",
                          name: "custnr",
                        },
                        {
                          label: "Material #",
                          name: "matnr",
                        },
                        {
                          label: "Description",
                          name: "desc",
                        },
                        {
                          label: "Sold Quantity",
                          name: "qty",
                        },                                                                                                                                                                                                                        
                      ]}
                      data={state.tableData}
                    />
        </Paper>
      </div>
    </ResponsiveDrawer>
  );
}
